import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties, ReactNode } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProposalExtraItemType } from '@econtractor/types';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import { CountBadge } from 'modules/warehouse';
import FormInput from 'components/forms/FormInput';
import SortableDragHandle from '../SortableDragHandle';
import {
    formatPrimaryPrice,
    formatRateLine,
    getSaleUsd,
    shouldShowRateLine,
} from '../model/templatePriceUtils';
import type useAdditionalItemsTable from './model/useAdditionalItemsTable';
import SolarIcon from 'components/icons/SolarIcon';

type AdditionalItemsTableApi = ReturnType<typeof useAdditionalItemsTable>;

export interface AdditionalItemsTableRowProps {
    row: ProposalExtraItemType;
    index: number;
    rate: number;
    localeNs: string;
    disabled: boolean;
    showAsOtherExpense?: boolean;
    table: AdditionalItemsTableApi;
    sortableId?: string;
}

interface AdditionalItemsTableRowViewProps extends Omit<AdditionalItemsTableRowProps, 'sortableId'> {
    dragHandle?: ReactNode;
    rowRef?: (node: HTMLElement | null) => void;
    rowStyle?: CSSProperties;
    rowClassName?: string;
}

const AdditionalItemsTableRowView = ({
    row,
    index,
    rate,
    localeNs,
    disabled,
    showAsOtherExpense = false,
    table,
    dragHandle,
    rowRef,
    rowStyle,
    rowClassName,
}: AdditionalItemsTableRowViewProps) => {
    const { t } = useTranslation([localeNs, 'common']);
    const {
        editNameIndex,
        editPriceIndex,
        editCountIndex,
        setEditNameIndex,
        setEditPriceIndex,
        setEditCountIndex,
        openNameEdit,
        openPriceEdit,
        savePriceEdit,
        openCountEdit,
        saveCountEdit,
        setAsOtherExpense,
        updateRow,
        removeRow,
        clearInlineEdits,
    } = table;

    return (
        <tr ref={rowRef} style={rowStyle} className={rowClassName}>
            {dragHandle ? <td className="drag">{dragHandle}</td> : null}
            <td className="name" data-label={t('edit.additional.tableItem')}>
                {editNameIndex === index && !disabled ? (
                    <FormInput
                        compact
                        showForm
                        setShowForm={(show) => {
                            if (!show) setEditNameIndex(null);
                        }}
                        defaultValue={row.itemName || ''}
                        callBack={(value) => {
                            const nextName = String(value || '').trim();
                            if (nextName) {
                                updateRow(index, { itemName: nextName });
                            }
                            setEditNameIndex(null);
                        }}
                    />
                ) : (
                    <div
                        className={`fw-medium text-break ${!disabled ? 'cursor-pointer' : ''}`}
                        onClick={() => !disabled && openNameEdit(index)}
                    >
                        {row.itemName}
                    </div>
                )}
            </td>
            <td className="price" data-label={t('edit.additional.tablePrice')}>
                {editPriceIndex === index && !disabled ? (
                    <FormInput
                        compact
                        showForm
                        setShowForm={(show) => {
                            if (!show) setEditPriceIndex(null);
                        }}
                        defaultValue={getSaleUsd(row)}
                        decimalPlaces={2}
                        callBack={(value) => savePriceEdit(index, value)}
                    />
                ) : (
                    <>
                        <p
                            className={`text-muted f-12 mb-0 my-1 ${!disabled ? 'cursor-pointer text-break' : ''}`}
                            onClick={() => !disabled && openPriceEdit(index)}
                        >
                            {formatPrimaryPrice(getSaleUsd(row), rate)}
                        </p>
                        {shouldShowRateLine(rate) && (
                            <p className="text-muted f-12 mb-0 my-1">
                                {formatRateLine(getSaleUsd(row), rate)}
                            </p>
                        )}
                    </>
                )}
            </td>
            {showAsOtherExpense ? (
                <td className="other" data-label={t('edit.additional.tableAsOtherExpense')}>
                    {disabled ? (
                        row.asOtherExpense ? (
                            <span className="badge bg-light-secondary text-secondary">
                                {t('edit.additional.tableAsOtherExpense')}
                            </span>
                        ) : null
                    ) : (
                        <Form.Check
                            type="switch"
                            id={`as-other-expense-${index}`}
                            className="mb-0"
                            checked={Boolean(row.asOtherExpense)}
                            onChange={(e) => setAsOtherExpense(index, e.target.checked)}
                        />
                    )}
                </td>
            ) : null}
            <td className="count text-end fw-medium" data-label={t('edit.additional.tableCount')}>
                {editCountIndex === index && !disabled ? (
                    <FormInput
                        compact
                        showForm
                        setShowForm={(show) => {
                            if (!show) setEditCountIndex(null);
                        }}
                        defaultValue={Number(row.itemCount || 0)}
                        decimalPlaces={2}
                        callBack={(value) => saveCountEdit(index, value)}
                    />
                ) : (
                    <div className="d-flex justify-content-end align-items-center">
                        <div
                            onClick={() => !disabled && openCountEdit(index)}
                            className={!disabled ? 'cursor-pointer' : ''}
                        >
                            <CountBadge count={safeFloatParse(row.itemCount || 0) || 0} />
                        </div>
                    </div>
                )}
            </td>
            {!disabled ? (
                <td className="action text-end" data-label={t('edit.additional.tableActions')}>
                    <Button
                        type="button"
                        size="sm"
                        className="py-0 px-1 mb-1"
                        variant="light-danger"
                        onClick={() => {
                            clearInlineEdits();
                            removeRow(index);
                        }}
                    >
                        <SolarIcon name="trash3" />
                    </Button>
                </td>
            ) : null}
        </tr>
    );
};

const SortableAdditionalItemsTableRow = ({
    sortableId,
    ...props
}: AdditionalItemsTableRowProps & { sortableId: string }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: sortableId,
    });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <AdditionalItemsTableRowView
            {...props}
            rowRef={setNodeRef}
            rowStyle={style}
            rowClassName={isDragging ? 'sortable-row--dragging' : undefined}
            dragHandle={<SortableDragHandle attributes={attributes} listeners={listeners} />}
        />
    );
};

const AdditionalItemsTableRow = ({ sortableId, ...props }: AdditionalItemsTableRowProps) => {
    if (sortableId) {
        return <SortableAdditionalItemsTableRow sortableId={sortableId} {...props} />;
    }
    return <AdditionalItemsTableRowView {...props} />;
};

export default AdditionalItemsTableRow;
